export const CSS_CUSTOM_PROPERTIES_COLORS = [
  'color-white',
  'color-off-white',
  'color-neutral-0',
  'color-neutral-10',
  'color-neutral-20',
  'color-neutral-30',
  'color-neutral-40',
  'color-neutral-50',
  'color-neutral-60',
  'color-neutral-70',
  'color-positive-70',
  'color-soft-black',
  'color-sembo-purple',
  'color-brand-pop',
  'color-brand-pop-hover',
  'color-brand-violet',
  'color-brand-summer',
  'color-brand-summer-hover',
  'color-brand-coral',
  'color-brand-jungle',
  'color-brand-jungle-dark',
  'color-brand-jungle-light',
  'color-brand-sky',
  'color-brand-lilac',
  'color-brand-lagoon',
  'color-information-light',
  'color-light-hover',
  'color-information-dark',
  'color-neutral-light',
  'color-neutral-dark',
  'color-error-light',
  'color-error-dark',
  'color-warning-light',
  'color-warning-dark',
  'color-success-light',
  'color-success-dark',
] as const;

export type CssCustomPropertyColor =
  (typeof CSS_CUSTOM_PROPERTIES_COLORS)[number];

export const CSS_CUSTOM_PROPERTIES_SPACING = [
  'spacing-2',
  'spacing-4',
  'spacing-8',
  'spacing-12',
  'spacing-16',
  'spacing-20',
  'spacing-24',
  'spacing-32',
  'spacing-40',
  'spacing-48',
  'spacing-64',
  'spacing-80',
  'spacing-96',
] as const;

export type CssCustomPropertySpacing =
  (typeof CSS_CUSTOM_PROPERTIES_SPACING)[number];

export type CssCustomPropertyBorderRadius =
  | 'border-radius-4'
  | 'border-radius-8'
  | 'border-radius-12'
  | 'border-radius-20'
  | 'border-radius-30';

export type CssCustomPropertyFont =
  | 'font-link-s-light'
  | 'font-link-l-light'
  | 'font-body-s-bold'
  | 'font-body-s-light'
  | 'font-body-m-bold'
  | 'font-body-m-light'
  | 'font-body-l-bold'
  | 'font-body-l-light'
  | 'font-label-s-bold'
  | 'font-label-s-light'
  | 'font-label-m-bold'
  | 'font-label-m-light'
  | 'font-label-l-bold'
  | 'font-label-l-light'
  | 'font-label-xl-bold'
  | 'font-label-xl-light'
  | 'font-heading-6'
  | 'font-heading-5'
  | 'font-heading-4'
  | 'font-heading-3'
  | 'font-heading-2'
  | 'font-heading-1'
  | 'font-title-label-l';

export const CSS_CUSTOM_PROPERTIES_FONT_SIZE = [
  'font-size-s',
  'font-size-m',
  'font-size-l',
  'font-size-xl',
  'font-size-h5',
  'font-size-h4',
  'font-size-h3',
  'font-size-h2',
  'font-size-h1',
] as const;

export type CssCustomPropertyFontSize =
  (typeof CSS_CUSTOM_PROPERTIES_FONT_SIZE)[number];

export type CssCustomPropertyZIndex =
  | 'panel-first-level-z-index'
  | 'modal-overlay-z-index'
  | 'modal-z-index';

export type CustomPropertyName =
  | CssCustomPropertyColor
  | CssCustomPropertySpacing
  | CssCustomPropertyBorderRadius
  | CssCustomPropertyFont
  | CssCustomPropertyFontSize
  | CssCustomPropertyZIndex;

export function getCssCustomPropertyValue(
  propertyName: CustomPropertyName
): string {
  return getComputedStyle(document.documentElement).getPropertyValue(
    `--${propertyName}`
  );
}

export function getStringStyles(
  color: CssCustomPropertyColor | undefined,
  getter: (variableName: CustomPropertyName) => string
): { color: string } {
  return {
    color: color ? getter(color) : '',
  };
}

export class CSSCustomProperty {
  public static get colorWhite(): string {
    return getCssCustomPropertyValue('color-white');
  }

  public static get colorOffWhite(): string {
    return getCssCustomPropertyValue('color-off-white');
  }

  public static get colorNeutral0(): string {
    return getCssCustomPropertyValue('color-neutral-0');
  }

  public static get colorNeutral10(): string {
    return getCssCustomPropertyValue('color-neutral-10');
  }

  public static get colorNeutral20(): string {
    return getCssCustomPropertyValue('color-neutral-20');
  }

  public static get colorNeutral30(): string {
    return getCssCustomPropertyValue('color-neutral-30');
  }

  public static get colorNeutral40(): string {
    return getCssCustomPropertyValue('color-neutral-40');
  }

  public static get colorNeutral50(): string {
    return getCssCustomPropertyValue('color-neutral-50');
  }

  public static get colorNeutral60(): string {
    return getCssCustomPropertyValue('color-neutral-60');
  }

  public static get colorNeutral70(): string {
    return getCssCustomPropertyValue('color-neutral-70');
  }

  public static get colorPositive70(): string {
    return getCssCustomPropertyValue('color-positive-70');
  }

  public static get colorSoftBlack(): string {
    return getCssCustomPropertyValue('color-soft-black');
  }

  public static get colorSemboPurple(): string {
    return getCssCustomPropertyValue('color-sembo-purple');
  }

  public static get colorBrandPop(): string {
    return getCssCustomPropertyValue('color-brand-pop');
  }

  public static get colorBrandPopHover(): string {
    return getCssCustomPropertyValue('color-brand-pop-hover');
  }

  public static get colorBrandViolet(): string {
    return getCssCustomPropertyValue('color-brand-violet');
  }

  public static get colorBrandSummer(): string {
    return getCssCustomPropertyValue('color-brand-summer');
  }

  public static get colorBrandSummerHover(): string {
    return getCssCustomPropertyValue('color-brand-summer-hover');
  }

  public static get colorBrandCoral(): string {
    return getCssCustomPropertyValue('color-brand-coral');
  }

  public static get colorBrandJungle(): string {
    return getCssCustomPropertyValue('color-brand-jungle');
  }

  public static get colorBrandJungleDark(): string {
    return getCssCustomPropertyValue('color-brand-jungle-dark');
  }

  public static get colorBrandJungleLight(): string {
    return getCssCustomPropertyValue('color-brand-jungle-light');
  }

  public static get colorBrandSky(): string {
    return getCssCustomPropertyValue('color-brand-sky');
  }

  public static get colorBrandLilac(): string {
    return getCssCustomPropertyValue('color-brand-lilac');
  }

  public static get colorBrandLagoon(): string {
    return getCssCustomPropertyValue('color-brand-lagoon');
  }

  public static get colorInformationLight(): string {
    return getCssCustomPropertyValue('color-information-light');
  }

  public static get colorLightHover(): string {
    return getCssCustomPropertyValue('color-light-hover');
  }

  public static get colorInformationDark(): string {
    return getCssCustomPropertyValue('color-information-dark');
  }

  public static get colorNeutralLight(): string {
    return getCssCustomPropertyValue('color-neutral-light');
  }

  public static get colorNeutralDark(): string {
    return getCssCustomPropertyValue('color-neutral-dark');
  }

  public static get colorErrorLight(): string {
    return getCssCustomPropertyValue('color-error-light');
  }

  public static get colorErrorDark(): string {
    return getCssCustomPropertyValue('color-error-dark');
  }

  public static get colorWarningLight(): string {
    return getCssCustomPropertyValue('color-warning-light');
  }

  public static get colorWarningDark(): string {
    return getCssCustomPropertyValue('color-warning-dark');
  }

  public static get colorSuccessLight(): string {
    return getCssCustomPropertyValue('color-success-light');
  }

  public static get colorSuccessDark(): string {
    return getCssCustomPropertyValue('color-success-dark');
  }
}
